class ProductForm {
  constructor(form) {
    this.form = form;
    this.productId = form.dataset.productId;

    const jsonEl = document.getElementById(`ProductJSON-${this.productId}`);
    if (!this.productId || !jsonEl) return;

    try {
      this.product = JSON.parse(jsonEl.textContent);
    } catch {
      return;
    }

    this.variantInput = form.querySelector('input[name="id"]');
    this.priceEl = form.querySelector('.productPrice');
    this.sellingPlanInputs = Array.from(form.querySelectorAll('[name="selling_plan"]'));
    this.quantityInput = form.querySelector('.purchase-quantity-input, [name="quantity"]');
    this.quantityText = form.querySelector('.purchase-quantity-text');
    this.addToCartButton = form.querySelector('button.addToCart');
    this.featuredImage = document.querySelector('.current-product-image');

    form.addEventListener('change', () => this.update());
    form.addEventListener('click', (event) => this.onQuantityClick(event));
    this.update();
  }

  isOneTime() {
    return Boolean(this.form.querySelector('.one-time-purchase-radio:checked'));
  }

  getSellingPlanId() {
    const selected = this.sellingPlanInputs.find(
      (input) => input.type !== 'radio' || input.checked
    );
    return selected?.value || '';
  }

  getQuantity() {
    return Math.max(1, parseInt(this.quantityInput?.value, 10) || 1);
  }

  getSelectedOptions() {
    const selected = [];
    this.form.querySelectorAll('[data-option-index]').forEach((input) => {
      if (input.matches('input[type="radio"]') && !input.checked) return;
      selected[Number(input.dataset.optionIndex)] = input.value;
    });
    return selected;
  }

  findVariant() {
    const selected = this.getSelectedOptions();
    return (
      this.product.variants.find((variant) =>
        selected.every((value, index) => {
          if (!value) return true;
          return (variant.options?.[index] || variant[`option${index + 1}`]) === value;
        })
      ) || this.product.variants[0]
    );
  }

  formatMoney(cents) {
    if (window.CurrencyUtils?.formatMoney) return window.CurrencyUtils.formatMoney(cents);
    return (cents / 100).toFixed(2);
  }

  onQuantityClick(event) {
    const decrement = event.target.closest('.quantity-decrement');
    const increment = event.target.closest('.quantity-increment');
    if ((!decrement && !increment) || !this.quantityInput) return;

    event.preventDefault();
    let qty = this.getQuantity();
    if (increment) qty += 1;
    if (decrement) qty = Math.max(1, qty - 1);
    this.quantityInput.value = String(qty);
    if (this.quantityText) this.quantityText.textContent = String(qty);
    this.syncStickyForm(this.findVariant());
  }

  updatePrice(variant) {
    const isOneTime = this.isOneTime();
    let subscriptionPrice = variant.price;

    if (variant.selling_plan_allocations?.length) {
      const planId = this.getSellingPlanId();
      const allocation =
        variant.selling_plan_allocations.find(
          (item) => String(item.selling_plan_id) === String(planId)
        ) || variant.selling_plan_allocations[0];
      subscriptionPrice = allocation.price;
    }

    if (this.priceEl) {
      this.priceEl.textContent = this.formatMoney(isOneTime ? variant.price : subscriptionPrice);
    }

    const discount =
      variant.price > subscriptionPrice
        ? Math.round(((variant.price - subscriptionPrice) / variant.price) * 100)
        : 0;

    this.form.querySelectorAll('.js-compare-price').forEach((el) => {
      el.textContent = this.formatMoney(variant.price);
    });
    this.form.querySelectorAll('.js-discount-percent').forEach((el) => {
      el.textContent = String(discount);
    });
    // Runs after update() has applied the subscription/one-time visibility, so only hide here.
    if (discount === 0) {
      this.form.querySelectorAll('.js-has-discount').forEach((el) => el.classList.add('hidden'));
    }
  }

  updateRecurringSummary(variant) {
    const priceEl = this.form.querySelector('.js-recurring-price');
    const frequencyEl = this.form.querySelector('.js-recurring-frequency');
    if (!priceEl && !frequencyEl) return;

    const selected = this.sellingPlanInputs.find(
      (input) => input.type === 'radio' && input.checked
    );
    if (!selected) return;

    const { recurringPrices, recurringFrequency } = selected.dataset;
    let { recurringPrice } = selected.dataset;
    try {
      recurringPrice = JSON.parse(recurringPrices || '{}')[variant.id] || recurringPrice;
    } catch {}
    if (priceEl && recurringPrice) priceEl.textContent = recurringPrice;
    if (frequencyEl && recurringFrequency) frequencyEl.textContent = recurringFrequency;
  }

  updateFeaturedImage(variant) {
    const image = variant.featured_image || variant.image;
    const imageSrc = typeof image === 'string' ? image : image?.src;
    if (!imageSrc || this.form.dataset.mainForm !== 'true' || !this.featuredImage) return;

    const picture = this.featuredImage.closest('picture');
    picture?.querySelectorAll('source').forEach((source) => {
      source.srcset = imageSrc;
    });
    this.featuredImage.srcset = imageSrc;
    this.featuredImage.src = imageSrc;
    this.featuredImage.alt = variant.title || '';
  }

  syncStickyForm(variant) {
    if (this.form.dataset.mainForm !== 'true') return;

    const sticky = document.querySelector(
      `form[data-sticky-form="true"][data-product-id="${this.productId}"]`
    );
    if (!sticky) return;

    const idInput = sticky.querySelector('input[name="id"]');
    if (idInput) idInput.value = variant.id;

    const quantityInput = sticky.querySelector('input[name="quantity"]');
    if (quantityInput) quantityInput.value = String(this.getQuantity());

    const sellingPlanInput = sticky.querySelector('input[name="selling_plan"]');
    if (sellingPlanInput) {
      const planId = this.isOneTime() ? '' : this.getSellingPlanId();
      sellingPlanInput.value = planId;
      sellingPlanInput.disabled = !planId;
    }

    const titleEl = sticky.querySelector('.formStickyScent');
    if (titleEl) titleEl.textContent = variant.title || '';

    const imageEl = sticky.querySelector('.sticky-featured-image');
    const image = variant.featured_image || variant.image;
    const imageSrc = typeof image === 'string' ? image : image?.src;
    if (imageEl && imageSrc) {
      const url = new URL(imageSrc, window.location.href);
      url.searchParams.set('width', '100');
      url.searchParams.set('height', '100');
      imageEl.src = url.toString();
      imageEl.alt = variant.title || '';
    }

    const button = sticky.querySelector('button.addToCart');
    if (button) {
      button.disabled = !variant.available;
      button.textContent = variant.available
        ? sticky.dataset.i18nAddToCart || 'Add to cart'
        : sticky.dataset.i18nSoldOut || 'Sold out';
    }
  }

  update() {
    const variant = this.findVariant();
    if (!variant) return;

    if (this.variantInput) this.variantInput.value = variant.id;

    const isOneTime = this.isOneTime();
    this.form.querySelectorAll('.js-subscription-only').forEach((el) => {
      el.classList.toggle('hidden', isOneTime);
    });
    this.form.querySelectorAll('.js-one-time-purchase').forEach((el) => {
      el.classList.toggle('hidden', !isOneTime);
    });
    this.sellingPlanInputs.forEach((input) => {
      input.disabled = isOneTime;
    });

    if (this.quantityText) this.quantityText.textContent = String(this.getQuantity());

    this.updatePrice(variant);
    this.updateRecurringSummary(variant);
    this.updateFeaturedImage(variant);
    this.syncStickyForm(variant);

    const selectedColor = this.form.querySelector('.color-option:checked');
    const selectedColorLabel = this.form.querySelector('.selectedColor');
    if (selectedColor && selectedColorLabel) {
      selectedColorLabel.textContent = selectedColor.value;
    }

    if (this.addToCartButton) {
      this.addToCartButton.disabled = !variant.available;
      this.addToCartButton.textContent = variant.available
        ? this.form.dataset.i18nAddToCart || 'Add to cart'
        : this.form.dataset.i18nSoldOut || 'Sold out';
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('form[action*="cart/add"][data-product-id]:not([data-sticky-form])').forEach((form) => {
    new ProductForm(form);
  });
});
